<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { OrderItemType } from '@/types/orderItemType'
import { formatCurrency } from '@/classes/Helpers.ts'

interface OrderNotice {
  feeId: number
  studentAccountId: number
  familyAccountId: number | null
  feeCategory: string
  amount: number
  type: string
  code: string
  message: string
}

const props = defineProps({
  orderNumber: {
    type: Number,
    required: true
  },
  order: {
    type: Array as PropType<TOrderItem[]>,
    required: true
  },
  finalizeCheckout: {
    type: Boolean,
    required: false,
    default: false
  },
  notices: {
    type: Array as PropType<OrderNotice[]>,
    required: false,
    default: () => []
  }
})

const getNoticeForItem = (item: TOrderItem): OrderNotice | undefined => {
  if (!props.notices || props.notices.length === 0) {
    return undefined
  }
  return props.notices.find(notice => notice.feeId === item.feeId)
}

const isAlreadyPaidNotice = (item: TOrderItem): boolean => {
  const notice = getNoticeForItem(item)
  return !!notice && notice.code === 'ALREADY_PAID_ERROR'
}

const emit = defineEmits(['removeItem'])

const removeItem = (item: TOrderItem) => {
  emit('removeItem', item)
}

const subtotal = computed(() => {
  let amount = 0
  props.order.forEach(item => {
    // Exclude items that have notices (e.g., already paid)
    if (!getNoticeForItem(item)) {
      amount += item.amount
    }
  })
  return formatCurrency(amount)
})
</script>

<template>
  <div>
    <div class="LabeledDivider">
      <span class="LabeledDivider_label">{{
        $t('c.checkout.order.orderLabel', { orderNumber: orderNumber })
      }}</span>
      <hr class="LabeledDivider_hr" />
    </div>

    <div
      v-for="item in order"
      :key="item.feeId"
      class="order-item-wrapper"
      :class="{
        'order-item-has-notice': getNoticeForItem(item) && !isAlreadyPaidNotice(item),
        'order-item-already-paid': isAlreadyPaidNotice(item)
      }"
    >
      <i v-if="isAlreadyPaidNotice(item)" class="pi pi-check-circle already-paid-icon"></i>
      <i
        v-else-if="getNoticeForItem(item)"
        class="pi pi-exclamation-triangle order-item-notice-icon"
      ></i>
      <div class="order-item-content">
        <div v-if="isAlreadyPaidNotice(item)" class="already-paid-row">
          {{ item.name }} <span class="already-paid-text">&mdash; Already paid</span>
        </div>
        <template v-else>
          <div class="grid mb-1 align-items-center">
            <div
              v-if="item.orderItemType === OrderItemType.ProcessingFee"
              class="col-7 flex align-items-center"
              style="color: #a20000"
            >
              {{ item.name }}
            </div>
            <div v-else class="col-7 flex align-items-center">
              {{ item.name }}
            </div>

            <template v-if="item.originalAmount === null || item.originalAmount === item.amount">
              <div
                v-if="item.orderItemType === OrderItemType.ProcessingFee"
                class="col-3 flex align-items-center justify-content-end pr-3"
                style="color: #a20000; font-weight: bold"
              >
                {{ formatCurrency(item.amount) }}
              </div>
              <div v-else class="col-3 flex align-items-center justify-content-end">
                {{ formatCurrency(item.amount) }}
              </div>
            </template>
            <template v-else>
              <div class="col-3 flex align-items-center justify-content-end">
                {{ formatCurrency(item.amount) }}
              </div>
            </template>
            <div class="col-2 flex align-items-center justify-content-end pr-4">
              <Button
                v-if="item.orderItemType !== OrderItemType.ProcessingFee && !finalizeCheckout"
                icon="pi pi-trash"
                size="sm"
                severity="secondary"
                rounded
                @click="removeItem(item)"
              />
            </div>
          </div>
          <div v-if="getNoticeForItem(item)" class="order-item-notice-message">
            {{ getNoticeForItem(item)?.message }}
          </div>
        </template>
      </div>
    </div>
    <div class="grid mb-3 font-bold">
      <div class="col-7">{{ $t('c.checkout.order.orderSubtotalLabel') }}:</div>
      <div class="col-3 flex align-items-center justify-content-end">{{ subtotal }}</div>
    </div>
  </div>
</template>

<style scoped>
.LabeledDivider {
  display: flex;
  align-items: center;
  overflow-x: hidden;
  margin: 5px 0px 15px 0px;
}
.LabeledDivider_label {
  padding-right: 10px;
  white-space: nowrap;
  font-weight: bold;
}
.LabeledDivider_hr {
  display: inline-block;
  width: 100%;
  margin: 0;
}
.order-item-wrapper {
  margin-bottom: 0.5rem;
}
.order-item-content {
  flex: 1;
  min-width: 0;
}
.order-item-has-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  background-color: #fee2e2;
  border-radius: 0.375rem;
}
.order-item-notice-icon {
  flex-shrink: 0;
  color: #b91c1c;
  font-size: 1.125rem;
  margin-top: 0.125rem;
}
.order-item-notice-message {
  color: #b91c1c;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-top: 0.25rem;
}
.order-item-already-paid {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.25rem 0.75rem;
  opacity: 0.75;
}
.already-paid-icon {
  flex-shrink: 0;
  color: #16a34a;
  font-size: 1.125rem;
  margin-top: 0.125rem;
}
.already-paid-row {
  font-size: 0.95rem;
  line-height: 1.4;
  padding: 0.125rem 0;
}
.already-paid-text {
  color: #16a34a;
  font-weight: 500;
  font-size: 0.875rem;
  white-space: nowrap;
}
</style>
